import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Callout, Checkbox, FormField } from "@promptgatewaysadmin/ui";
import { useAuth, authErrorKey } from "../../auth/AuthContext.js";
import { safeNext } from "../../auth/safeNext.js";
import { readKeepSignedIn, writeKeepSignedIn } from "../../auth/persistence.js";
import { errorCopy } from "../../copy/errors.js";

/**
 * /login
 *
 * Two things on this screen are security controls rather than UX:
 *
 *  1. Wrong password and unknown user produce ONE message. Distinguishing them is account
 *     enumeration.
 *  2. `?next=` is validated as a same-origin RELATIVE path before use. An open redirect on
 *     a login screen is a real vulnerability, and this parameter is reachable from an email
 *     link.
 *
 * A `locked` org logs in normally. `locked` is a billing state, not an auth state — the
 * customer must be able to get in to pay us.
 */
export function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keep, setKeep] = useState(readKeepSignedIn);
  const [busy, setBusy] = useState(false);
  const [errKey, setErrKey] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrKey(null);
    try {
      // Persistence is applied BEFORE the sign-in call inside signInWithPassword.
      // Afterwards it would not retroactively move the session, and the user would have
      // unchecked a box that then still remembered them.
      const verified = await auth.signInWithPassword(email, password, keep);
      writeKeepSignedIn(keep);
      // Verified users go to `next`; unverified users go to /verify regardless. The guard
      // would do this anyway, but doing it here avoids a visible bounce. The flag comes
      // from the call's result — `auth.emailVerified` in this closure is still pre-login.
      navigate(verified ? next : "/verify", { replace: true });
    } catch (err) {
      setErrKey(keyFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Welcome back</h1>

      {errKey && (
        <Callout tone="danger" role="alert">
          {errorCopy(errKey).body}
        </Callout>
      )}


      <form onSubmit={submit} noValidate>
        <FormField
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <FormField
          label="Password"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />

        {/* (encrypted) sits INSIDE the label. Outside it, assistive tech reads only "Keep
            me signed in" and the disclosure is lost to exactly the users who cannot see it. */}
        <Checkbox
          label={
            <>
              Keep me signed in <span className="pg-auth__note">(encrypted)</span>
            </>
          }
          checked={keep}
          onChange={(e) => setKeep(e.currentTarget.checked)}
        />

        <Button type="submit" block disabled={busy}>
          {busy ? "Signing in…" : "Log in"}
        </Button>
      </form>

      <p className="pg-auth__alt">
        <Link to="/reset">Forgot your password?</Link>
      </p>
      <p className="pg-auth__alt">
        New here? <Link to="/signup">Create an account</Link>
      </p>
    </>
  );
}

function keyFor(err: unknown): string {
  const code = (err as { code?: string })?.code;
  return typeof code === "string" ? authErrorKey(code) : "error.auth.badCredentials";
}
